import { Badge, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@design-system';
import { X } from 'lucide-react';
import {
  ACTION_TAG_TOOLTIPS,
  ALGORITHMS_CURRENTLY_RUNNING,
  ALGORITHMS_ERROR,
  ALGORITHMS_FINISHED_RUNNING,
  CLIENT_COMPLETED_REVIEW,
  CLIENT_STARTED_REVIEW,
  COMPLETED_REVIEW_TOGETHER,
  INITIAL_CONTACT_MADE,
  INTAKE_FORM_INCOMPLETE,
  REPRESENTATIVE_ASSIGNED,
  REQUESTED_MEETING,
  REVIEW_CALL_SCHEDULED,
  START_ONBOARDING,
  STARTED_INTAKE_FORM,
  STARTED_REVIEW_TOGETHER,
  USER_AVAILABLE_STATUS_TAGS,
} from '@/util/constants';
import { useDispatch } from 'react-redux';
import {
  putClientByClientIdRequest,
  putClientOnboardingSlideProgressByClientIdRequest,
} from '@client';
import { newUuid } from '@shared';

interface ActionTagProps {
  className?: string;
  clientTag: string;
  client: Client;
}

const bgColorByTag: Record<string, string> = {
  [ALGORITHMS_CURRENTLY_RUNNING]: 'bg-accent',
  [ALGORITHMS_FINISHED_RUNNING]: 'bg-amber-100',
  [ALGORITHMS_ERROR]: 'bg-red-100',
  [CLIENT_STARTED_REVIEW]: 'bg-indigo-100',
  [CLIENT_COMPLETED_REVIEW]: 'bg-emerald-100',
  [STARTED_REVIEW_TOGETHER]: 'bg-blue-100',
  [COMPLETED_REVIEW_TOGETHER]: 'bg-green-100',
  [REVIEW_CALL_SCHEDULED]: 'bg-fuchsia-100',
  [INTAKE_FORM_INCOMPLETE]: 'bg-stone-100',
  [START_ONBOARDING]: 'bg-orange-100',
  [STARTED_INTAKE_FORM]: 'bg-blue-100',
  [REQUESTED_MEETING]: 'bg-rose-100',
  [REPRESENTATIVE_ASSIGNED]: 'bg-yellow-100',
  [INITIAL_CONTACT_MADE]: 'bg-sky-100',
};

const deletableTags = [
  ...USER_AVAILABLE_STATUS_TAGS,
  ALGORITHMS_FINISHED_RUNNING,
  START_ONBOARDING,
  REQUESTED_MEETING,
];

export function ActionTag({ clientTag, client }: ActionTagProps) {
  const dispatch = useDispatch();
  const onboardingSlideProgress = client.onboardingSlideProgress;

  const removeActionTag = (statusTag: string) => {
    const clientOnboardingSlideProgressId =
      onboardingSlideProgress?.clientOnboardingSlideProgressId ?? newUuid();
    const newTags = [...client.clientTags.filter(tag => tag && tag !== statusTag)];
    dispatch(putClientByClientIdRequest({ ...client, clientTags: newTags }));
    if (statusTag === STARTED_REVIEW_TOGETHER) {
      dispatch(
        putClientOnboardingSlideProgressByClientIdRequest({
          clientOnboardingSlideProgressId,
          clientId: client.clientId,
          hasClientStartedOnboarding: false,
        }),
      );
    }
    if (statusTag === COMPLETED_REVIEW_TOGETHER) {
      dispatch(
        putClientOnboardingSlideProgressByClientIdRequest({
          clientOnboardingSlideProgressId,
          clientId: client.clientId,
          hasClientCompletedOnboarding: false,
        }),
      );
    }
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={`text-foreground m-0.5 inline-flex items-center rounded-full py-0.5 pl-2 pr-1 text-xs font-normal ${bgColorByTag[clientTag] ?? 'bg-accent'}`}
          >
            <span className="whitespace-nowrap pr-1">{clientTag}</span>
            {deletableTags.includes(clientTag) && (
              <button
                onClick={() => removeActionTag(clientTag)}
                type="button"
                className="text-mute-foreground hover:bg-accent hover:text-foreground inline-flex size-4 shrink-0 items-center justify-center rounded-full"
                data-testid={`remove-status-${clientTag.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <span className="sr-only">Remove status for {clientTag}</span>
                <X className="size-2.5" />
              </button>
            )}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-64">
          <p>{ACTION_TAG_TOOLTIPS[clientTag]}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
